/** @jsx jsx */
import React, { useCallback, useEffect, useState } from 'react';
import { jsx } from '@compiled/react';
import { graphql, useFragment, type PreloadedQuery } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { cardKeys as spotlightCardKeys } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/spotlight-coordination/constants.tsx';
import { useIsActiveSpotlightCard } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/spotlight-coordination/index.tsx';
import SpotlightTourCard from '@atlassian/jira-issue-navigator-changeboarding/src/ui/spotlight-tour-card/index.tsx';
import SpotlightTourTarget, {
	type Props as SpotlightTourTargetProps,
} from '@atlassian/jira-issue-navigator-changeboarding/src/ui/spotlight-tour-target/index.tsx';
import { SEARCH_MODE_ADVANCED } from '@atlassian/jira-jql-builder/src/common/constants.tsx';
import type {
	SearchMode,
	SearchModeChangeReason,
} from '@atlassian/jira-jql-builder/src/common/types.tsx';
import { AI_FEATURE_NAME } from '@atlassian/jira-natural-language-to-jql/src/common/constants.tsx';
import NaturalLanguageToJQL, {
	type Props as NaturalLanguageToJQLProps,
} from '@atlassian/jira-natural-language-to-jql/src/ui/index.tsx';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { NaturalLanguageToJQLWrapper$key as UserPreferencesFragmentKey } from '@atlassian/jira-relay/src/__generated__/NaturalLanguageToJQLWrapper.graphql';
import type { NaturalLanguageToJQLWrapper_withAIFilters$key } from '@atlassian/jira-relay/src/__generated__/NaturalLanguageToJQLWrapper_withAIFilters.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { useJqlDebuggerStoreActions } from '@atlassian/jira-jql-debugger/src/controllers/jql-debugger-data/index.tsx';
import type { JQLBuilderWithAIFilterRefinementQuery } from '@atlassian/jira-relay/src/__generated__/JQLBuilderWithAIFilterRefinementQuery.graphql.ts';
import { useAITourActions } from '../../controllers/ai-tour/index.tsx';
import { usePlaceholders } from '../../controllers/use-placeholders/index.tsx';
import { AlertMessage } from './alert-message/index.tsx';
import JQLBuilderEmbedded, {
	type OmitJQLBuilderEmbeddedProps,
	type JQLBuilderEmbeddedProps,
} from './jql-builder-embedded/JQLBuilderEmbedded.tsx';
import messages from './messages.tsx';

const createUseDebouncedEventForAIResult = () => {
	const lastEventPublishedState: Record<string, 'PENDING' | 'PUBLISHED'> = {};
	let previousTimeoutId = setTimeout(() => ({}), 0);

	// Current debounce timeout is 2 seconds. This value was chosen by observation only. Feel free to update as necessary
	const DEBOUNCE_TIMEOUT = 2000;

	// To avoid any memory leaks, we clear the lastPublished entry after 10seconds
	// The idea here is that events can duplicated if multiple events for same promptId arrive 10seconds apart
	// In practice however, all events finish arriving within 500ms-1500ms
	const DUPLICATE_CHECK_CLEAR_TIMEOUT = 10000;

	return function useDebouncedReadyEvent() {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const fireEventForAIResult = useCallback(
			({
				isSuccess,
				is5xxError,
				promptId = '',
				errorOrigin = '',
			}: {
				isSuccess: boolean;
				is5xxError: boolean;
				errorOrigin?: string;
				promptId?: string;
			}) => {
				// Clear any pending timeouts so that the latest event data is used
				// If the promptId is different, then we don't need to clear the timeout, and just let the event go through
				if (lastEventPublishedState[promptId] === 'PENDING') {
					clearTimeout(previousTimeoutId);
				}

				if (lastEventPublishedState[promptId] === 'PUBLISHED') {
					// Event already published. Ignore the latest data and stick with the previously published event
					return;
				}

				// Mark that an event is pending for the particular promptId
				lastEventPublishedState[promptId] = 'PENDING';

				// Update the timeoutId, so that it can be cleared when the data becomes stale (with a newer event)
				previousTimeoutId = setTimeout(() => {
					if (isSuccess) {
						fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult viewed', {
							aiFeatureName: AI_FEATURE_NAME,
							singleInstrumentationID: promptId,
							userGeneratedAI: 1,
							proactiveAIGenerated: 0,
							isAIFeature: 1,
						});
					} else {
						fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult error', {
							aiFeatureName: AI_FEATURE_NAME,
							singleInstrumentationID: promptId,
							errorOrigin,
							userGeneratedAI: 1,
							proactiveAIGenerated: 0,
							isAIFeature: 1,
							is5xxError,
						});
					}
					// Mark the events corresponding to the latest promptId as published
					// So that any events that arrive after the debounce timeout are ignored
					// This is because having a unique `aiResult viewed` event is more important than having stale data (searchMode) in the event
					lastEventPublishedState[promptId] = 'PUBLISHED';

					// Clear lastEventPublished entry to avoid memory leaks
					setTimeout(() => {
						delete lastEventPublishedState[promptId];
					}, DUPLICATE_CHECK_CLEAR_TIMEOUT);
				}, DEBOUNCE_TIMEOUT);
			},
			[createAnalyticsEvent],
		);

		return { fireEventForAIResult };
	};
};

const useDebouncedReadyEventForAIResult = createUseDebouncedEventForAIResult();

type Props = OmitJQLBuilderEmbeddedProps<
	JQLBuilderEmbeddedProps,
	'generatedJql' | 'userPreferences' | 'filters'
> & {
	/**
	 * Event emitted when the back button is pressed in AI inline prompt component.
	 */
	onBack: () => void;
	userPreferences: UserPreferencesFragmentKey | null;
	filters: NaturalLanguageToJQLWrapper_withAIFilters$key | null;
	filterRefinementQueryReference?: PreloadedQuery<JQLBuilderWithAIFilterRefinementQuery> | null;
};
export const NaturalLanguageToJQLWrapper = ({
	onBack,
	userPreferences,
	filters,
	filterRefinementQueryReference,
	...props
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const [jqlTooComplex, setJqlTooComplex] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { onSearch } = props;
	const onPromptSuccess = useCallback(
		(generatedJql: string) => {
			onSearch && onSearch(generatedJql);
			setJqlTooComplex(false);
		},
		[onSearch],
	);

	const { onJourneyStart } = props;
	const onFocus = useCallback(() => {
		if (!onJourneyStart) return;

		const analyticsEvent = createAnalyticsEvent({
			action: 'focused',
			actionSubject: 'input',
		});
		const attributes = onJourneyStart();
		fireUIAnalytics(analyticsEvent, 'aiInlinePrompt', attributes);
	}, [createAnalyticsEvent, onJourneyStart]);

	const placeholders = usePlaceholders();

	const userPreferencesData = useFragment<UserPreferencesFragmentKey>(
		graphql`
			fragment NaturalLanguageToJQLWrapper on JiraUserPreferences {
				...JQLBuilderEmbedded
			}
		`,
		userPreferences,
	);

	const filtersData = useFragment<NaturalLanguageToJQLWrapper_withAIFilters$key>(
		graphql`
			fragment NaturalLanguageToJQLWrapper_withAIFilters on JiraQuery {
				...JQLBuilderEmbedded_withAIFilters
			}
		`,
		filters,
	);

	const { onEndAITour } = useAITourActions();
	const [isTourActive] = useIsActiveSpotlightCard([
		spotlightCardKeys.GLOBAL_SCOPE_JQL_BUILDER_NL_TO_JQL,
		spotlightCardKeys.JQL_BUILDER_NL_TO_JQL,
	]);

	const handleSearchModeChange = (searchMode: SearchMode, reason?: SearchModeChangeReason) => {
		setJqlTooComplex(searchMode === SEARCH_MODE_ADVANCED && reason === 'QueryUnsupported');
	};

	// Remove all the changeboarding on clean up of 'remove_nin_changeboarding_rll_usages', should be showing as empty fragments already
	const renderSpotlightCard: SpotlightTourTargetProps['renderSpotlightCard'] = ({
		onSetActiveCard,
		onEndTour,
	}) => (
		<>
			<SpotlightTourCard
				cardKey={spotlightCardKeys.GLOBAL_SCOPE_JQL_BUILDER_NL_TO_JQL}
				actions={[
					{
						onClick: () => {
							onSetActiveCard(spotlightCardKeys.GLOBAL_SCOPE_VIEW_SWITCHER);
						},
						text: formatMessage(messages.next),
					},
					{
						appearance: 'subtle',
						onClick: () => {
							onEndTour();
						},
						text: formatMessage(messages.skip),
					},
				]}
				heading={formatMessage(
					fg('jira-issue-terminology-update-m4')
						? messages.globalTourHeadingIssueTerminologyRefreshM4
						: messages.globalTourHeading,
				)}
				target="jql-builder-with-ai.natural-language-to-jql"
				targetRadius={8}
				targetBgColor={token('color.background.input')}
				testId="jql-builder-with-ai.ui.natural-language-to-jql-wrapper.spotlight"
				messageId="jql-builder-with-ai.ui.natural-language-to-jql-wrapper.spotlight-tour-card.jql-builder-with-ai.natural-language-to-jql.spotlight.1"
				messageType="transactional"
			>
				{formatMessage(
					fg('jira-issue-terminology-update-m4')
						? messages.globalTourBodyIssueTerminologyRefreshM4
						: messages.globalTourBody,
				)}
			</SpotlightTourCard>
			<SpotlightTourCard
				cardKey={spotlightCardKeys.JQL_BUILDER_NL_TO_JQL}
				actions={[
					{
						onClick: () => {
							// End the tour in AI tour store
							onEndAITour();
							// End the tour in NIN changeboarding store
							onEndTour();
						},
						text: formatMessage(messages.done),
					},
				]}
				heading={formatMessage(messages.tourHeading)}
				target="jql-builder-with-ai.natural-language-to-jql"
				targetRadius={8}
				targetBgColor={token('color.background.input')}
				testId="jql-builder-with-ai.ui.natural-language-to-jql-wrapper.spotlight"
				messageId="jql-builder-with-ai.ui.natural-language-to-jql-wrapper.spotlight-tour-card.jql-builder-with-ai.natural-language-to-jql.spotlight.2"
				messageType="transactional"
			>
				{formatMessage(messages.tourBody)}
			</SpotlightTourCard>
		</>
	);

	// eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
		setJqlTooComplex(false);
	}, [props.query]);

	const { fireEventForAIResult } = useDebouncedReadyEventForAIResult();

	const onNLEvent: NaturalLanguageToJQLProps['onNLEvent'] = useCallback(
		({ promptId, nlResponseType, is5xxError }) => {
			const jqlErrors = props.jqlMessages?.filter((message) => message.type === 'error') || [];

			fireEventForAIResult({
				isSuccess: nlResponseType === 'success' && !jqlErrors.length,
				is5xxError,
				promptId,
				errorOrigin:
					// eslint-disable-next-line no-nested-ternary
					nlResponseType === 'error'
						? 'NL Search Backend'
						: /* When the nlResponseType is success, it means the NL-to-JQL operation was successful
							 However, when props.jqlMessages?.length is not 0, that means there was an error with the jql generated
							 Hence it can be marked that the error is coming from NIN
						   */
							nlResponseType === 'success' && !!jqlErrors.length
							? 'NIN'
							: 'N/A',
			});
		},
		[fireEventForAIResult, props.jqlMessages],
	);

	const jqlDebuggerStoreActions = useJqlDebuggerStoreActions();

	/**
	 * When the NaturalLanguageToJQLWrapper component unmounts, we want to reset the issue navigator search mode in JQL debugger
	 * This component is mounted when the user is in Natural Language Mode
	 * Currently we use the issueNavigatorSearchMode to enable/disable the JQL debugger proactive experience
	 */
	useEffect(() => {
		return () => {
			jqlDebuggerStoreActions?.setIssueNavigatorSearchMode(undefined);
		};
	}, [jqlDebuggerStoreActions]);

	return (
		<SpotlightTourTarget
			name="jql-builder-with-ai.natural-language-to-jql"
			renderSpotlightCard={renderSpotlightCard}
		>
			<NaturalLanguageToJQL
				autoFocus
				backButtonLabel={formatMessage(messages.backButtonLabel)}
				onPromptSuccess={onPromptSuccess}
				onBack={onBack}
				onFocus={onJourneyStart ? onFocus : undefined}
				// Show static placeholder if spotlight tour is running
				placeholder={isTourActive ? placeholders[0] : placeholders}
				onNLEvent={onNLEvent}
			>
				{(generatedJql: string) => (
					<>
						<JQLBuilderEmbedded
							{...props}
							userPreferences={userPreferencesData ?? null}
							generatedJql={generatedJql}
							onUpdateSearchMode={handleSearchModeChange}
							// Remount component when generated JQL changes so screen viewed events are re-submitted
							key={generatedJql}
							filters={filtersData ?? null}
							filterRefinementQueryReference={filterRefinementQueryReference}
						/>
						{jqlTooComplex && (
							<AlertMessage
								appearance="info"
								testId="jql-builder-with-ai.ui.natural-language-to-jql-wrapper.jql-too-complex"
							>
								{formatMessage(messages.jqlTooComplex)}
							</AlertMessage>
						)}
					</>
				)}
			</NaturalLanguageToJQL>
		</SpotlightTourTarget>
	);
};
